import { useEffect, useMemo } from "react";
import {
  Button,
  ButtonGroup,
  Col,
  Form,
  InputGroup,
  Row,
} from "react-bootstrap";
import { Control, useController, useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import * as yup from "yup";
import type {
  AccountCashSetting,
  CashTargetCondition,
  CashTargetType,
} from "../../../api/src/cash-settings/lib";
import type { Model } from "../../../api/src/models/models.service";
import Loading from "../Loading";
import {
  useQueryAccount,
  useQueryAccountHoldings,
} from "../clients/account/lib";
import { useQueryHousehold } from "../clients/household/lib";
import AbsolutePercentToggle from "../components/AbsolutePercentToggle";
import FormError from "../components/FormError";
import FormFieldError from "../components/FormFieldError";
import HelpTooltip from "../components/HelpTooltip";
import SymbolField from "../components/SymbolField";
import {
  getSchemaFieldLabel,
  nullableNumberSchema,
  nullableStringSchema,
} from "../lib/forms";
import { formatCurrency, formatPercent } from "../lib/numbers";
import { CASH_SYMBOLS } from "../lib/security";
import { useQueryModel } from "../models/lib";
import {
  CASH_SPLIT_REQUIRED,
  CUSTODIAN_CASH_REFUSED,
  CUSTODIAN_CASH_SYMBOL,
  cashSplitPercentHelpMessage,
  cashSplitSymbolHelpMessage,
  hasCashSplit,
  isCustodianCashSymbol,
  modelContainsCashSplit,
  useQueryAccountCashSetting,
  useQueryCashSettings,
} from "./lib";

export type AccountCashSettingFormState = Omit<AccountCashSetting, "accountId">;

// The one of the three sentences this schema puts in front of an advisor that
// no other form uses — R17's completeness rule is the account's, not the firm
// template's. It names the form's own field label and says which value is
// missing, rather than calling the pair invalid; it is the api's wording for
// the same refusal, down to the full stop (the literal thrown by
// validateCashSplitPair, api/src/cash-settings/cash-settings.service.ts), so a
// rejection that reaches the server and one caught here read alike.
//
// The other two, CASH_SPLIT_REQUIRED and CUSTODIAN_CASH_REFUSED, sit in
// `./lib`, because the firm cash-settings form puts both in front of the
// advisor for the same rules.
const SPLIT_PERCENTAGE_REQUIRED =
  "Cash Split % of Cash Target is required when a Cash Split is set.";

/**
 * The fixed warning the Cash Split field carries (spec §4.1, ruled by Jon with
 * Davey 2026-09-17). §4.2 states the rule it reports.
 *
 * It is shown always and is conditional on nothing — neither on the security
 * chosen nor on the account's tax status — because the rule holds for every
 * cash split. The two budgets it names — Capital Gains and Tax — are the
 * account's own trading fields, labeled "ST Capital Gains Budget", "LT Capital
 * Gains Budget" and "Tax Budget"
 * (`client/src/clients/account/AccountModelTrading.tsx:88`, `:91`, `:95`).
 */
const CASH_SPLIT_GAIN_WARNING =
  "Rebalancing may sell this security at a gain. The account's Capital Gains and Tax Budgets do not limit that sale.";

/**
 * The account's effective model: its own, else its household's
 * (an-decision-01a0ee65bb4aad1a). It is the model the api's cash split save
 * compares R6 against (`COALESCE(a.model_id, h.model_id)`,
 * `api/src/lib/cash-split.ts`), so a collision the form finds is one the api
 * would refuse. `undefined` while loading, and when the account and its
 * household both have no model.
 *
 * The household is read only for an account with no model of its own.
 *
 * The model is read through useQueryModel rather than the models list:
 * getAllModels returns primarySecurities: [] for every model by design
 * (api/src/models/models.service.ts), so a collision check built on the list
 * would never fire. getModel populates them from model_primary_security.
 */
export function useEffectiveModel(accountId: number | undefined) {
  const { data: account } = useQueryAccount(accountId ?? -1, {
    includeBalances: true,
    enabled: typeof accountId !== "undefined",
  });

  const householdId =
    account?.assignedModelId == null ? account?.householdId : undefined;
  const { data: household } = useQueryHousehold(householdId ?? -1, {
    enabled: householdId != null,
  });

  const effectiveModelId =
    account?.assignedModelId ?? household?.assignedModelId;
  const { data: effectiveModel } = useQueryModel(effectiveModelId ?? -1, {
    enabled: effectiveModelId != null,
  });

  return effectiveModelId == null ? undefined : effectiveModel;
}

/**
 * R6's refusal (doc-cash-split-01a05d5f47df200b) for a cash split the model
 * contains, or `undefined` when it does not: a model containing the cash split
 * cannot be assigned to an account configured with that cash split.
 *
 * The sentence is `AccountModelTrading`'s for the same collision, and the one
 * the api answers this form's save with (`refusalMessage`,
 * `api/src/lib/cash-split.ts`), so a collision caught here and one the api
 * refuses read alike.
 *
 * AccountCashInfo renders it beside the field, and both save handlers
 * (AccountCashBillingSettings, HouseholdAccountCashSettings) stop on it, so a
 * save that stops has already said why.
 */
export function cashSplitModelRefusal(
  model: Pick<Model, "name" | "primarySecurities"> | undefined,
  cashSplitSymbol: string | null | undefined,
) {
  return typeof model === "undefined" ||
    !modelContainsCashSplit(model.primarySecurities, cashSplitSymbol)
    ? undefined
    : `Model ${model.name} contains ${cashSplitSymbol?.trim()}, this account's Cash Split. A model containing the cash split cannot be assigned to this account.`;
}

/**
 * The seven Fidelity fund tickers (doc-cash-split-01a05d5f47df200b §1 Terms,
 * R12): `CASH_SYMBOLS` less `CASH-1`, which is plain cash and never a cash
 * sweep security. The api tests a `cashSweepSymbol` against the same set
 * (`validateCashSweepSymbol`, `api/src/cash-settings/cash-settings.service.ts`).
 */
const FIDELITY_FUND_TICKERS: readonly string[] = CASH_SYMBOLS.filter(
  (symbol) => symbol !== CUSTODIAN_CASH_SYMBOL,
);

const foldSymbol = (symbol: string) => symbol.trim().toUpperCase();

const isFidelityFundTicker = (symbol: string | null | undefined) =>
  typeof symbol === "string" &&
  FIDELITY_FUND_TICKERS.includes(foldSymbol(symbol));

/**
 * The tickers the Cash Sweep field offers besides the empty option, in
 * alphabetical order: each held Fidelity fund ticker and the stored one when
 * it is a Fidelity fund ticker (R27; the stored one whether or not the account
 * holds it, D6), less the cash split and each primary of the effective model
 * (R28). `cashSplitSymbol` is the live form value, so the options follow the
 * Cash Split field as the advisor types.
 */
export function cashSweepOptions({
  heldSymbols,
  storedSymbol,
  cashSplitSymbol,
  model,
}: {
  heldSymbols: readonly string[];
  storedSymbol: string | null | undefined;
  cashSplitSymbol: string | null | undefined;
  model: Pick<Model, "primarySecurities"> | undefined;
}) {
  const cashSplit =
    typeof cashSplitSymbol === "string" ? foldSymbol(cashSplitSymbol) : null;

  return [
    ...new Set(
      [...heldSymbols, ...(storedSymbol == null ? [] : [storedSymbol])]
        .filter(isFidelityFundTicker)
        .map(foldSymbol),
    ),
  ]
    .filter(
      (symbol) =>
        symbol !== cashSplit &&
        !modelContainsCashSplit(model?.primarySecurities, symbol),
    )
    .sort();
}

const cashConditionSchema = yup
  .string()
  .oneOf<CashTargetCondition>(["and", "or"])
  .default("and")
  .required();

export const schema: yup.ObjectSchema<AccountCashSettingFormState> = yup.object(
  {
    cashSettingId: nullableNumberSchema.label("Cash Setting"),
    cashTargetType: yup
      .string()
      .oneOf<CashTargetType>(["absolute", "percent"])
      .default("percent")
      .required()
      .label("Target Type"),
    cashTargetAbsolute: nullableNumberSchema.min(0).label("Cash Target"),
    cashTargetPercent: nullableNumberSchema
      .min(0)
      .max(100)
      .label("Percent Target"),
    cashTargetMinCondition: cashConditionSchema.label("Minimum Condition"),
    cashTargetMinAbsolute: nullableNumberSchema
      .min(0)
      .label("Minimum Cash Trigger"),
    cashTargetMinPercent: nullableNumberSchema
      .min(0)
      .max(100)
      .label("Minimum Percent Trigger"),
    cashTargetMaxCondition: cashConditionSchema.label("Maximum Condition"),
    cashTargetMaxAbsolute: nullableNumberSchema
      .min(0)
      .label("Maximum Cash Trigger"),
    cashTargetMaxPercent: nullableNumberSchema
      .min(0)
      .max(100)
      .label("Maximum Percent Trigger"),
    // The empty option is "", which nullableStringSchema sends as null: the
    // account names no cash sweep security (R39). The key is sent on every
    // save, so the stored value stays through its own option (D6).
    cashSweepSymbol: nullableStringSchema.label("Cash Sweep"),
    // R11 and R17 (doc-cash-split-01a05d5f47df200b) between them make the pair
    // all or nothing, and this schema carries both halves.
    //
    // R11 rejects a split percentage with no cash split. R17, ruled by Jon on
    // 2026-09-16, rejects the mirror image: "The account form must require both
    // halves of the pair." Either half can arrive alone because the two values
    // have separate scopes — cash_setting.cash_split_percent is per cash
    // setting, while the split itself is chosen here — so a percentage or a
    // symbol can be typed on its own, or seeded alone from an assigned setting.
    //
    // R17's own 50% default is not made dead by this. It covers an account row
    // that carries a cash split and no percentage whatever wrote it, and this
    // form is not the only writer.
    //
    // WHY THE MIRROR CLAUSE IS A .test() AND NOT A SECOND .when(). Two fields
    // that .when() on each other are a cycle to yup, and it refuses the schema
    // outright rather than resolving it — `Cyclic dependency, node was:
    // "cashSplitSymbol"`, thrown for every value, valid ones included. A
    // .test() reads its sibling off `this.parent` and declares no edge, so the
    // graph stays acyclic. `this.parent` is the cast object, so the sibling has
    // already been through nullableStringSchema's "" -> null transform.
    cashSplitPercent: nullableNumberSchema
      .min(0)
      .max(100)
      .label("Cash Split % of Cash Target")
      .test({
        name: "split-percentage-required-with-cash-split",
        message: SPLIT_PERCENTAGE_REQUIRED,
        test(cashSplitPercent) {
          const cashSplitSymbol: unknown = this.parent?.cashSplitSymbol;

          // CASH-1 is refused as a cash split whatever sits beside it, so
          // asking for a percentage as well would send the advisor back to a
          // pair the schema refuses anyway. The api orders its own rules the
          // same way, and for the same reason (validateCashSplitPair,
          // api/src/cash-settings/cash-settings.service.ts).
          if (!hasCashSplit(cashSplitSymbol)) {
            return true;
          }
          if (isCustodianCashSymbol(cashSplitSymbol)) {
            return true;
          }

          return (
            cashSplitPercent !== null && typeof cashSplitPercent !== "undefined"
          );
        },
      }),
    // WHY THE SYMBOL SIDE ASKS hasCashSplit AND NOT yup's .required(). The two
    // sides of the pair have to decide the same question the same way, and the
    // percentage side decides it through hasCashSplit, which trims.
    // .required() does not trim, and nullableStringSchema transforms only
    // undefined, null and "" to null (`client/src/lib/forms.ts`), so a cash
    // split field holding "   " satisfied .required(), passed the form, and
    // drew a 400 from the api — which trims before deciding the same question
    // (validateCashSplitPair). Routing both sides through hasCashSplit is what
    // makes the form and the api agree on what counts as a cash split.
    //
    // The clause still fires for an absent value: yup runs a .test() for null
    // and undefined as readily as for a string, which is where it differs from
    // .required(), so an empty field is refused exactly as it was before.
    cashSplitSymbol: nullableStringSchema
      .label("Cash Split")
      .when("cashSplitPercent", {
        is: (cashSplitPercent: number | null | undefined) =>
          cashSplitPercent !== null && typeof cashSplitPercent !== "undefined",
        then: (schema) =>
          schema.test({
            name: "cash-split-required-with-split-percentage",
            message: CASH_SPLIT_REQUIRED,
            test: (cashSplitSymbol) => hasCashSplit(cashSplitSymbol),
          }),
        otherwise: (schema) => schema,
      })
      // R15 admits any security as a cash split, so this is the one named
      // symbol it keeps out and not a membership test against
      // FIDELITY_CASH_EQUIVALENT_SYMBOLS or any other list.
      .test({
        name: "cash-split-is-not-custodian-cash",
        message: CUSTODIAN_CASH_REFUSED,
        test: (cashSplitSymbol) => !isCustodianCashSymbol(cashSplitSymbol),
      }),
  },
);

const ConditionToggle = ({
  name,
  control,
}: {
  name: keyof AccountCashSettingFormState;
  control: Control<AccountCashSettingFormState>;
}) => {
  const {
    field: { onChange, value },
  } = useController({
    name,
    control,
  });

  return (
    <ButtonGroup>
      <Button
        variant={value === "and" ? "primary" : "light"}
        style={
          value !== "and"
            ? { opacity: 0.6, textDecoration: "line-through", color: "#6c757d" }
            : undefined
        }
        onClick={() => onChange("and")}
      >
        AND
      </Button>
      <Button
        variant={value === "or" ? "primary" : "light"}
        style={
          value !== "or"
            ? { opacity: 0.6, textDecoration: "line-through", color: "#6c757d" }
            : undefined
        }
        onClick={() => onChange("or")}
      >
        OR
      </Button>
    </ButtonGroup>
  );
};

const AccountCashInfo = () => {
  const {
    register,
    reset,
    watch,
    control,
    formState: { errors },
    getValues,
    setValue,
  } = useFormContext<AccountCashSettingFormState>();

  const { accountId: accountIdStr } = useParams();
  const accountId =
    typeof accountIdStr === "undefined" ? undefined : parseInt(accountIdStr);

  const {
    isPending: isPendingAccountCashSettings,
    isError: isErrorAccountCashSettings,
    data: accountCashSetting,
  } = useQueryAccountCashSetting(accountId ?? -1, {
    enabled: typeof accountId !== "undefined",
  });

  const {
    isPending: isPendingAccount,
    isError: isErrorAccount,
    data: account,
  } = useQueryAccount(accountId ?? -1, {
    includeBalances: true,
    enabled:
      !isPendingAccountCashSettings &&
      typeof accountCashSetting !== "undefined",
  });

  const effectiveModel = useEffectiveModel(accountId);

  const {
    isPending: isPendingCashSettings,
    isError: isErrorCashSettings,
    data: dataCashSettings,
  } = useQueryCashSettings();

  const cashSettingOptions = isPendingCashSettings
    ? [
        <option key={null} value="">
          Loading...
        </option>,
      ]
    : isErrorCashSettings
      ? [
          <option key={null} value="">
            There was an error loading cash settings
          </option>,
        ]
      : [
          <option key="" value="">
            None
          </option>,
          ...[...(dataCashSettings?.data ?? [])]
            .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
            .map((cashSetting) => (
              <option key={cashSetting.id} value={cashSetting.id}>
                {cashSetting.name}
              </option>
            )),
        ];

  useEffect(() => {
    if (typeof accountCashSetting !== "undefined") {
      reset({ ...getValues(), ...accountCashSetting });
    }
  }, [accountCashSetting, reset, register, getValues]);

  const cashTargetType = watch("cashTargetType");
  const cashSettingId = watch("cashSettingId", null) as string | null;
  const cashSettingIdField = register("cashSettingId");

  // R6 (doc-cash-split-01a05d5f47df200b): a model containing the cash split
  // cannot be assigned to an account configured with that cash split. This is
  // the alert beside the field the advisor would change, and it reads the live
  // form value so a split typed here raises it without a save first. The two
  // save handlers stop on the same refusal (cashSplitModelRefusal).
  //
  // AccountCashBillingSettings and HouseholdAccountCashSettings render this
  // one component, so both routes get the alert with no route check
  // (an-decision-01a061fc3a398a08).
  const cashSplitSymbol = watch("cashSplitSymbol");
  const cashSplitRefusal = cashSplitModelRefusal(
    effectiveModel,
    cashSplitSymbol,
  );

  // The Cash Sweep field (doc-cash-split-01a05d5f47df200b R26 to R28, §4.1):
  // a select over the held Fidelity fund tickers, at every custodian.
  const { data: holdings, isError: isErrorHoldings } = useQueryAccountHoldings(
    accountId,
    "",
  );
  const heldSymbols = useMemo(
    () =>
      (holdings ?? [])
        .filter((holding) => holding.units !== 0)
        .map((holding) => holding.security?.symbol ?? "")
        .map(foldSymbol),
    [holdings],
  );
  const storedCashSweepSymbol = accountCashSetting?.cashSweepSymbol;
  const cashSweepSymbolOptions = useMemo(
    () =>
      cashSweepOptions({
        heldSymbols,
        storedSymbol: storedCashSweepSymbol,
        cashSplitSymbol,
        model: effectiveModel,
      }),
    [heldSymbols, storedCashSweepSymbol, cashSplitSymbol, effectiveModel],
  );
  const {
    field: { value: cashSweepSymbol, onChange: onChangeCashSweepSymbol },
  } = useController({ name: "cashSweepSymbol", control });

  // A select shows only a value it offers, so a value it does not offer --
  // a stored CASH-1 (R27), or a ticker that became the cash split or a
  // primary of the model (R28) -- is the empty option on screen and in what
  // the form saves alike.
  useEffect(() => {
    if (
      typeof cashSweepSymbol === "string" &&
      !cashSweepSymbolOptions.includes(cashSweepSymbol)
    ) {
      setValue("cashSweepSymbol", null);
    }
  }, [cashSweepSymbol, cashSweepSymbolOptions, setValue]);

  const cashSweepNote =
    typeof holdings === "undefined"
      ? isErrorHoldings
        ? "The account's holdings did not load, so the Cash Sweep field offers no ticker the account holds."
        : null
      : cashSweepSymbolOptions.length === 0
        ? "This account holds no Fidelity fund ticker that can be its cash sweep security."
        : typeof cashSweepSymbol === "string" &&
            cashSweepSymbol === storedCashSweepSymbol &&
            !heldSymbols.includes(cashSweepSymbol)
          ? `This account does not hold ${cashSweepSymbol}, its cash sweep security.`
          : null;

  return isPendingAccountCashSettings || isPendingAccount ? (
    <Loading />
  ) : isErrorAccountCashSettings || isErrorAccount ? (
    <FormError message="Failed to load account cash settings" />
  ) : !accountCashSetting ? (
    <FormError message="Account cash settings not found" />
  ) : (
    <>
      <Row className="mb-2">
        <Col xl={6} md={12} sm={6}>
          <Row>
            <Col as="dt" sm={2}>
              AUM
            </Col>
            <Col as="dd" sm={10}>
              {formatCurrency(account?.accountBalance ?? 0, 2)}
            </Col>
          </Row>
        </Col>
        <Col xl={6} md={12} sm={6}>
          <Row>
            <Col as="dt" sm={2}>
              Cash*
            </Col>
            <Col as="dd" sm={10}>
              {formatCurrency(account?.cashBalance ?? 0, 2)} (
              {formatPercent(
                (account?.cashBalance ?? 0) /
                  (account?.accountBalance ?? account?.cashBalance ?? 1),
                2,
              )}
              )
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col>
          <Row className="mb-3">
            <Col>
              <Form.Label>
                {getSchemaFieldLabel(schema.fields.cashSettingId)}
              </Form.Label>
              <Form.Select
                {...cashSettingIdField}
                onChange={(event) => {
                  void cashSettingIdField.onChange(event);

                  const selected = dataCashSettings?.data.find(
                    (cashSetting) =>
                      String(cashSetting.id) === event.target.value,
                  );

                  // The chosen setting seeds the split once, as a starting
                  // value; the account owns both fields from then on. Hanging
                  // this on the selector's own change event is what keeps the
                  // seeding user-initiated -- the reset() above replays the
                  // account's saved values without dispatching one.
                  //
                  // "None", or a list that has not loaded, therefore changes
                  // the assignment alone: the split values belong to the
                  // account and may have been edited since.
                  if (typeof selected !== "undefined") {
                    setValue("cashSplitPercent", selected.cashSplitPercent);
                    setValue("cashSplitSymbol", selected.cashSplitSymbol);
                  }
                }}
              >
                {cashSettingOptions}
              </Form.Select>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Label htmlFor="cashSweepSymbol">
                {getSchemaFieldLabel(schema.fields.cashSweepSymbol)}
              </Form.Label>
              <Form.Select
                id="cashSweepSymbol"
                name="cashSweepSymbol"
                value={cashSweepSymbol ?? ""}
                onChange={(event) =>
                  onChangeCashSweepSymbol(
                    event.target.value === "" ? null : event.target.value,
                  )
                }
              >
                <option value=""></option>
                {cashSweepSymbolOptions.map((symbol) => (
                  <option key={symbol} value={symbol}>
                    {symbol}
                  </option>
                ))}
              </Form.Select>
              <FormFieldError field={errors.cashSweepSymbol} />
              {cashSweepNote === null ? null : (
                <Form.Text className="text-muted d-block">
                  {cashSweepNote}
                </Form.Text>
              )}
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Label>
                {getSchemaFieldLabel(schema.fields.cashSplitPercent)}
                <HelpTooltip
                  tooltip={cashSplitPercentHelpMessage}
                  buttonProps={{
                    width: 16,
                    className: "ms-1 align-text-bottom",
                  }}
                />
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  {...register("cashSplitPercent")}
                />
                <InputGroup.Text>%</InputGroup.Text>
              </InputGroup>
              <FormFieldError field={errors.cashSplitPercent} />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Label>
                {getSchemaFieldLabel(schema.fields.cashSplitSymbol)}
                <HelpTooltip
                  tooltip={cashSplitSymbolHelpMessage}
                  buttonProps={{
                    width: 16,
                    className: "ms-1 align-text-bottom",
                  }}
                />
              </Form.Label>
              <SymbolField
                fieldName="cashSplitSymbol"
                register={register}
                control={control}
                getValues={getValues}
              />
              <FormFieldError field={errors.cashSplitSymbol} />
              <Form.Text className="text-warning-emphasis d-block">
                {CASH_SPLIT_GAIN_WARNING}
              </Form.Text>
            </Col>
          </Row>
          {typeof cashSplitRefusal === "undefined" ? null : (
            <Row className="mb-3">
              <Col>
                <FormError message={cashSplitRefusal} />
              </Col>
            </Row>
          )}
          {/*
            The cash target and its triggers resolve live from an assigned cash
            setting, so they vanish once one is chosen. The split above does
            not: the account's own columns are the only operative values, so
            both fields stay put either way.
          */}
          {cashSettingId !== "" && cashSettingId !== null ? null : (
            <>
              <Row className="mb-3">
                <Col>
                  <Form.Label>
                    {getSchemaFieldLabel(schema.fields.cashTargetAbsolute)}
                  </Form.Label>
                  <InputGroup>
                    {cashTargetType !== "absolute" ? null : (
                      <>
                        <Form.Control
                          type="number"
                          min={0}
                          step={0.01}
                          {...register("cashTargetAbsolute")}
                        />
                        <FormFieldError field={errors.cashTargetAbsolute} />
                      </>
                    )}
                    {cashTargetType !== "percent" ? null : (
                      <>
                        <Form.Control
                          type="number"
                          min={0}
                          max={100}
                          step={0.01}
                          {...register("cashTargetPercent")}
                        />
                        <FormFieldError field={errors.cashTargetPercent} />
                      </>
                    )}
                    <AbsolutePercentToggle
                      // {...register("cashTargetType")}
                      name="cashTargetType"
                      control={control}
                    />
                  </InputGroup>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Label>
                    {getSchemaFieldLabel(schema.fields.cashTargetMinAbsolute)}
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      min={0}
                      step={0.01}
                      {...register("cashTargetMinAbsolute")}
                    />
                    <ConditionToggle
                      name="cashTargetMinCondition"
                      control={control}
                    />
                    <Form.Control
                      type="number"
                      min={0}
                      max={100}
                      step={0.01}
                      {...register("cashTargetMinPercent")}
                    />
                    <InputGroup.Text>%</InputGroup.Text>
                  </InputGroup>
                  <FormFieldError field={errors.cashTargetMinCondition} />
                  <FormFieldError field={errors.cashTargetMinAbsolute} />
                  <FormFieldError field={errors.cashTargetMinPercent} />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Label>
                    {getSchemaFieldLabel(schema.fields.cashTargetMaxAbsolute)}
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      min={0}
                      step={0.01}
                      {...register("cashTargetMaxAbsolute")}
                    />
                    <ConditionToggle
                      name="cashTargetMaxCondition"
                      control={control}
                    />
                    <Form.Control
                      type="number"
                      min={0}
                      max={100}
                      step={0.01}
                      {...register("cashTargetMaxPercent")}
                    />
                    <InputGroup.Text>%</InputGroup.Text>
                  </InputGroup>
                  <FormFieldError field={errors.cashTargetMaxCondition} />
                  <FormFieldError field={errors.cashTargetMaxAbsolute} />
                  <FormFieldError field={errors.cashTargetMaxPercent} />
                </Col>
              </Row>
            </>
          )}
        </Col>
      </Row>
    </>
  );
};

export default AccountCashInfo;
